import type {
    DmMessagePatientDetail,
    DmMessagePatientTriage
} from '../../../../services/dmMessagesInbox';

// How long a new patient message may wait for the AI before the view calls it untriaged.
export const AI_TRIAGE_GRACE_MS = 10 * 60_000;

export type AiReplyState =
    | { kind: 'draft'; reply: string; basedOn: string | null; newSinceDraft: boolean }
    | { kind: 'noReplyNeeded' }
    | { kind: 'draftUnavailable' }
    | { kind: 'notTriaged' }
    | { kind: 'none' };

export type AiReplyTriage = Pick<
    DmMessagePatientTriage,
    'suggestedReply' | 'basedOnQuickReply' | 'replyNeeded' | 'aiBasisMessageAt'
>;

export interface AiReplyInput {
    triage: AiReplyTriage;
    // The newest unreviewed patient message; null once all are reviewed.
    latestMessageAt: string | null;
    now: number;
}

// Everything but the clock, so the view that ticks can be the only one re-rendering.
export interface AiReplySource {
    triage: AiReplyTriage & Pick<DmMessagePatientTriage, 'suggestedReplyGeneratedAt'>;
    latestMessageAt: string | null;
}

interface AiReplyPatient {
    triage: AiReplySource['triage'];
    latestMessage: Pick<NonNullable<DmMessagePatientDetail['latestMessage']>, 'receivedAt'> | null;
}

// Needs the patient detail: list rows carry no suggested reply text.
export function toAiReplySource({ triage, latestMessage }: AiReplyPatient): AiReplySource {
    return { triage, latestMessageAt: latestMessage?.receivedAt ?? null };
}

function toTime(iso: string | null): number | null {
    if (!iso) return null;
    const time = Date.parse(iso);
    return Number.isNaN(time) ? null : time;
}

// False when either side is missing or unparseable: no evidence of a newer message.
export function isNewerThan(later: string | null, earlier: string | null): boolean {
    const laterTime = toTime(later);
    const earlierTime = toTime(earlier);
    return laterTime !== null && earlierTime !== null && laterTime > earlierTime;
}

// True when a patient message exists that the AI's last run didn't read.
export function isAiBasisBehind(latestMessageAt: string | null, basisAt: string | null): boolean {
    if (toTime(latestMessageAt) === null) return false;
    return toTime(basisAt) === null || isNewerThan(latestMessageAt, basisAt);
}

export function isPastAiGrace(latestMessageAt: string | null, now: number): boolean {
    const received = toTime(latestMessageAt);
    return received !== null && now - received > AI_TRIAGE_GRACE_MS;
}

function draftState(reply: string, { triage, latestMessageAt }: AiReplyInput): AiReplyState {
    return {
        kind: 'draft',
        reply,
        basedOn: triage.basedOnQuickReply?.trim() || null,
        newSinceDraft: isNewerThan(latestMessageAt, triage.aiBasisMessageAt)
    };
}

// A verdict on older messages is hidden: it says nothing about the newest one.
export function aiReplyState(input: AiReplyInput): AiReplyState {
    const { triage, latestMessageAt, now } = input;
    const reply = triage.suggestedReply?.trim();
    if (reply) return draftState(reply, input);
    if (isAiBasisBehind(latestMessageAt, triage.aiBasisMessageAt)) {
        return isPastAiGrace(latestMessageAt, now) ? { kind: 'notTriaged' } : { kind: 'none' };
    }
    if (triage.replyNeeded === false) return { kind: 'noReplyNeeded' };
    if (triage.replyNeeded === true) return { kind: 'draftUnavailable' };
    return { kind: 'none' };
}
